import { useMemo, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import type { ProductSourceChunk } from '@/shared/hooks/api/productSource'
import type { ProductSourcePreviewMode } from '../utils/isRagIngestedProductSource'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

type ProductSourceChunksWithPreviewProps = {
  chunks: ProductSourceChunk[]
  downloadUrl?: string
  previewMode: ProductSourcePreviewMode
}

type ChunkCardProps = {
  chunk: ProductSourceChunk
}

type ProductSourcePageViewProps = {
  page: number
  pageChunks: ProductSourceChunk[]
  previewMode: ProductSourcePreviewMode
  downloadUrl?: string
  showPdfPage: boolean
  showImagePreview: boolean
  onImagePreviewError: () => void
}

function groupChunksByPage(
  chunks: ProductSourceChunk[]
): Map<number, ProductSourceChunk[]> {
  const grouped = new Map<number, ProductSourceChunk[]>()

  for (const chunk of chunks) {
    const existing = grouped.get(chunk.page) ?? []
    existing.push(chunk)
    grouped.set(chunk.page, existing)
  }

  return grouped
}

function ChunkCard({ chunk }: ChunkCardProps): ReactElement {
  const { t } = useTranslation('productCatalog')

  return (
    <Card>
      <CardHeader className='space-y-1 pb-2'>
        <CardTitle className='text-sm font-medium'>
          {t('sources.inspect.chunkPart', {
            index: chunk.index,
            page: chunk.page,
            section: chunk.section,
          })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <CardDescription className='whitespace-pre-wrap text-sm font-normal text-foreground'>
          {chunk.rawText}
        </CardDescription>
      </CardContent>
    </Card>
  )
}

function ProductSourcePageView({
  page,
  pageChunks,
  previewMode,
  downloadUrl,
  showPdfPage,
  showImagePreview,
  onImagePreviewError,
}: ProductSourcePageViewProps): ReactElement {
  const { t } = useTranslation('productCatalog')

  const showPdfPreview = previewMode === 'pdf' && showPdfPage
  const showImage = previewMode === 'image' && showImagePreview && downloadUrl

  return (
    <div className='space-y-3'>
      <h3 className='text-sm font-medium text-muted-foreground'>
        {t('sources.inspect.pageLabel', { page })}
      </h3>
      <div className='flex flex-col gap-4 lg:flex-row'>
        {showPdfPreview && (
          <div className='shrink-0 rounded-md border bg-muted/30 p-2'>
            <Page pageNumber={page} width={400} />
          </div>
        )}
        {showImage && (
          <div className='shrink-0 rounded-md border bg-muted/30 p-2'>
            <img
              src={downloadUrl}
              alt={t('sources.inspect.imagePreviewAlt')}
              className='max-h-[480px] max-w-full object-contain'
              onError={onImagePreviewError}
            />
          </div>
        )}
        <div className='flex min-w-0 flex-1 flex-col gap-3'>
          {pageChunks.map((chunk) => (
            <ChunkCard key={chunk.index} chunk={chunk} />
          ))}
        </div>
      </div>
    </div>
  )
}

export function ProductSourceChunksWithPdf({
  chunks,
  downloadUrl,
  previewMode,
}: ProductSourceChunksWithPreviewProps): ReactElement {
  const { t } = useTranslation('productCatalog')
  const [failedPreviewUrl, setFailedPreviewUrl] = useState<string | undefined>(
    undefined
  )

  const chunksByPage = useMemo(() => groupChunksByPage(chunks), [chunks])
  const sortedPages = useMemo(
    () => Array.from(chunksByPage.keys()).sort((left, right) => left - right),
    [chunksByPage]
  )

  const firstPage = sortedPages[0]
  const showPdfDocument =
    previewMode === 'pdf' &&
    downloadUrl != null &&
    failedPreviewUrl !== downloadUrl
  const showImagePreview =
    previewMode === 'image' &&
    downloadUrl != null &&
    failedPreviewUrl !== downloadUrl

  const handlePreviewError = () => {
    if (downloadUrl != null) {
      setFailedPreviewUrl(downloadUrl)
    }
  }

  const renderPageGroups = (showPdfPage: boolean) =>
    sortedPages.map((page) => (
      <ProductSourcePageView
        key={page}
        page={page}
        pageChunks={chunksByPage.get(page) ?? []}
        previewMode={previewMode}
        downloadUrl={downloadUrl}
        showPdfPage={showPdfPage}
        showImagePreview={showImagePreview && page === firstPage}
        onImagePreviewError={handlePreviewError}
      />
    ))

  return (
    <div className='space-y-6'>
      <h2 className='text-base font-semibold'>
        {t('sources.inspect.chunksTitle')}
      </h2>
      {showPdfDocument && (
        <Document file={downloadUrl} onLoadError={handlePreviewError}>
          <div className='space-y-8'>{renderPageGroups(true)}</div>
        </Document>
      )}
      {!showPdfDocument && (
        <div className='space-y-8'>{renderPageGroups(false)}</div>
      )}
    </div>
  )
}
